// DEF-216 — no white page. Before this, an error while drawing a screen (a screen file that failed to load, or a bug)
// made React clear the whole app and leave a white page (Thomas saw one on staging 2026-11-02 after the #313 deploy).
// Now the screen says it did not load and offers Reload; the menu stays. lib/chunk-reload.ts still reloads a stale tab
// by itself once a minute; this is what shows when it lets an error through (a second failure within the minute, or a
// browser that blocks session storage) and for any other error. Used twice: around each screen inside the app shell
// (keyed by the path, so going to another screen clears it) and around the whole app in main.tsx.
import React from 'react'

/** True for the browsers' messages when a screen's file could not be loaded (Chrome, Firefox, Safari, Vite). */
export function isLoadError(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error)
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading chunk/i.test(text)
}

interface Props {
  children: React.ReactNode
  /** Reloads the page; replaced in tests. */
  onReload?: () => void
  /** 'page' inside the app shell (the menu stays), 'app' around everything. */
  scope?: 'page' | 'app'
}
interface State { error: unknown }

export class PageErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: unknown): State {
    return { error: error ?? new Error('Unknown error') }
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    console.error('DEF-216: a screen failed to draw', { loadError: isLoadError(error), error, componentStack: info.componentStack })
  }

  render() {
    if (!this.state.error) return this.props.children
    const loadError = isLoadError(this.state.error)
    const reload = this.props.onReload ?? (() => window.location.reload())
    return (
      <div role="alert" style={this.props.scope === 'app' ? S.full : S.page}>
        <div style={S.card}>
          <h1 style={S.h1}>{loadError ? 'This page did not load' : 'Something went wrong on this page'}</h1>
          <p style={S.muted}>
            {loadError
              ? 'RestaurantSuite was probably updated while this tab was open. Reloading gets the new version.'
              : 'Reloading usually fixes it. If it happens again, tell us what you clicked.'}
          </p>
          <button type="button" style={S.btn} onClick={reload}>Reload</button>
        </div>
      </div>
    )
  }
}

const S: Record<string, React.CSSProperties> = {
  page: { padding: 24, display: 'flex', justifyContent: 'center' },
  full: { minHeight: '100vh', padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg, #f8fafc)' },
  card: { maxWidth: 460, background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e2e8f0)', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 10 },
  h1: { fontSize: 18, fontWeight: 700, color: 'var(--color-text, #0f172a)', margin: 0 },
  muted: { fontSize: 13, color: 'var(--color-text-muted, #64748b)', margin: 0, lineHeight: 1.5 },
  btn: { padding: '8px 14px', background: 'var(--color-primary, #1e40af)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' },
}
